import Controller from '@ember/controller';
import { action } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
// eslint-disable-next-line import-x/default
import containerSizing, {
  type ContainerSizing,
} from 'frontend-utils/decorators/container-sizing';
import type { Ota } from 'frontend-utils/models';
import type { RoomType } from 'frontend-utils/models/roomtype';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import type { Period } from 'lumen/models';
import type HotelPricingManagerRoute from 'lumen/routes/hotel/pricing-manager';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  HEADER_HEIGHT,
  type MealTypeValues,
  type MembershipType,
  type RateParam,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  MAX_ALLOWED_MONTHS_IN_FUTURE,
  OPEN_PRICING_ROOM_CATEGORY_TO_RI_ROOMTYPE_MAPPING,
  type OpenPricingRoomCategoryToRIRoomTypes,
  type OpenRoomPricingFixedCategory,
  PRIMA_VIEWS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  DETAIL_TABS,
  RATES_PAGE_MODES,
} from 'lumen/utils/constants/rate-constants';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';
import { getCalendarMonthDateRanges } from 'lumen/utils/pricing-assistant/generate-calendar-daterange';
import {
  selectedRoomCategory,
  validateOpenPricingCalendarParams,
} from 'lumen/utils/pricing-assistant/open-pricing-utils';
import type { ModelFrom } from 'lumen/utils/routes';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface HotelPricingManagerController extends ContainerSizing, Controller {}

@containerSizing({ headerHeight: 1 })
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class HotelPricingManagerController extends Controller {
  declare model: ModelFrom<HotelPricingManagerRoute>;
  queryParams = [
    'month',
    'week',
    'rateParam',
    'otaId',
    'maxPersons',
    'mealPlan',
    'roomType',
    'los',
    'platform',
    'compsetId',
    'membershipType',
    'view',
    'refDaysAgo',
    'rateCode',
  ];

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare localStorage: Services['local-storage'];
  @service('marketing/recommendation')
  declare marketingRecommendationService: Services['marketing/recommendation'];
  @service declare navigation: Services['navigation'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare roiTrophies: Services['roi-trophies'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Height of PA PMS self service banners (if present). Required to take into account while calculating content heights
  @tracked bannerHeight = 0;
  @tracked compsetId = DEFAULT_QP_VALUES.hotel['pricing-manager'].compsetId;
  @tracked dayOverrideModalPeriod: Period | null = null;
  @tracked isShowingCalendarSettingsModal = false;
  @tracked lastWeekSelected: string | null = null;
  @tracked los: number = DEFAULT_QP_VALUES.hotel['pricing-manager'].los;
  @tracked maxPersons: string | null = null;
  @tracked mealPlan: MealTypeValues | null = null;
  @tracked membershipType: MembershipType =
    DEFAULT_QP_VALUES.hotel['pricing-manager'].membershipType;
  @tracked month: string | null = null;
  @tracked otaId: string | null = null;

  @tracked platform: Platform =
    DEFAULT_QP_VALUES.hotel['pricing-manager'].platform;
  // Currently only used in DRP table view
  @tracked rateCode: string | null = null;
  @tracked rateParam: RateParam | null = null;

  // Currently only used in ORP table view
  @tracked refDaysAgo: number =
    DEFAULT_QP_VALUES.hotel['pricing-manager'].refDaysAgo;
  @tracked roomType: string | null = null;
  @tracked view: string | null = null;
  @tracked week: string | null = null;

  /*
    Tracked object to store the state banners when the user dismisses it
    Why? When user dismisses a banner, we store this information in localStorage directly.
    But making changes to the localStorage doesn't trigger reactivity and as a result UI doesn't update accordingly i.e. the banner doesn't disappear.
    Therefore, we also update this information in a tracked object in order to trigger reactivity.
  */
  bannerVisibilityStates = trackedObject<Record<string, boolean>>();
  // Makes sure the main loading bar is positioned underneath all fixed headers on the page
  mainLoadingBarPositionTop = HEADER_HEIGHT + SUB_HEADER_HEIGHT;
  pageMode = RATES_PAGE_MODES.PRICING;

  get arePricingPageModeRequestsLoading(): boolean {
    return Boolean(
      this.model.requestPricingAssistantPmsInfoTaskResult?.isRunning ||
      this.model.pricingAssistantGeneralDataRequestsTaskResult.isRunning ||
      this.model.pricingAssistantRecommendationsRequestTaskResult.isRunning ||
      this.model.pricingAssistantRatesRequestTaskResult.isRunning ||
      this.model.requestPricingAssistantSubscriptionMetaTaskResult.isRunning ||
      this.model.requestPricingAssistantRecommendationReasonsTaskResult
        ?.isRunning ||
      this.marketingRecommendationService.fetchRecommendationsTask.isRunning ||
      this.model.pricingAssistantCompetitorsRatesRequestTaskResult?.isRunning ||
      this.model.pricingAssistantroomOffsetHierarchyTaskResult?.isRunning,
    );
  }

  get autopilotBannerlocalStorageKey(): string {
    return `isPricingManagerAutopilotBannerVisible_${this.hotelAndMarketService.hotel.id}`;
  }

  // Reserve space for the 2 main headers + 3rd new filter bar header + main content padding bottom + main content padding top + calender header
  get calendarReservedHeight(): number {
    let effectiveHeight = HEADER_HEIGHT + SUB_HEADER_HEIGHT + 16 + 8 + 30;

    if (this.bannerHeight) {
      // Reserve extra space for banner height + 16px margin bottom on banner wrapper element
      effectiveHeight += this.bannerHeight + 16;
    }

    return effectiveHeight;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsForCurrentHotelAndOTA() {
    const { competitors } = this.hotelAndMarketService.activeCompset ?? {};

    if (!competitors?.length) {
      return null;
    }

    return competitors.filter(
      (competitor) =>
        competitor.otas.find((ota) => ota.id === this.otaId) !== undefined,
    );
  }

  /* Ideally we won't need another getter for the periods, since the periods are already filled when requesting carates
   But the period id between rates and carates request are different, having rates periods id including the LOS and the carates periods id only showing the date.
   This is because of how data is handled in the BE for RI.
   Taking into consideration the differences, we need to have a separate getter for the competitors periods
  */
  get competitorsPeriods(): Period[] {
    if (this.isCompetitorsRatesRequestLoading) {
      const dateRangeStart = this.monthMoment.startOf('month').startOf('week');
      const dateRangeEnd = this.monthMoment
        .endOf('month')
        .endOf('week')
        .startOf('day');

      // Push a dummy period object for every day in range to have a clean loading state while periods request is loading #DUMMYPERIOD
      return getPeriodsInRange(dateRangeStart, dateRangeEnd, 'day').map(
        () => ({}),
      ) as Period[];
    }

    return (
      this.model.pricingAssistantCompetitorsRatesRequestTaskResult?.value ?? []
    );
  }

  get dateForDatePicker(): Dayjs {
    if (this.isTableView) {
      return this.weekMoment;
    }

    return this.monthMoment;
  }

  get datepickerStandardRange(): 'month' | 'week' {
    return this.isCalendarView ? 'month' : 'week';
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.dateForDatePicker.startOf(this.datepickerStandardRange),
      end: this.dateForDatePicker

        .endOf(this.datepickerStandardRange)
        .startOf('day'),
    };
  }

  get flattenedRoomOffsetHierarchy(): string[] {
    if (this.isRoomOffsetHierarchyStrategyLoading) {
      return [];
    }

    const hierarchy =
      this.model.pricingAssistantroomOffsetHierarchyTaskResult?.value;

    // No room hierarchy configured
    if (!hierarchy) {
      return [];
    }

    return hierarchy;
  }

  // TODO-BULK-UPDATE: Remove hasPricingAssistantOpenPricingFeature check when ORP is implemented
  get hasBulkUpdateRights(): boolean {
    return (
      this.access.hasPricingAssistantEditRights &&
      this.access.hasPricingAssistantViewRights &&
      !this.access.hasPricingAssistantOpenPricingFeature
    );
  }

  get hasPricingAndDistribution(): boolean {
    return (
      this.access.hasFeature('app.channel_manager') &&
      this.access.hasFeature('app.pricing_assistant')
    );
  }

  get isAutopilotBannerNotDismissed(): boolean {
    // If bannerVisibilityStates has the key, return value from there
    const cachedState =
      this.bannerVisibilityStates[this.autopilotBannerlocalStorageKey];
    if (cachedState !== undefined) {
      return cachedState;
    }

    // Else, fetch autopilot banner state from local storage
    const localStorageAutopilotBannerVisibleValue = this.localStorage.getItem(
      this.autopilotBannerlocalStorageKey,
    );
    if (localStorageAutopilotBannerVisibleValue) {
      return JSON.parse(localStorageAutopilotBannerVisibleValue) as boolean;
    }

    return true;
  }

  get isAutopilotEnabledInSettings(): boolean {
    return Boolean(
      this.hotelAndMarketService.hotel.paAutopilotSettings?.['enabled'],
    );
  }

  // Also wait on recommendations: cells derive their "pushing" state from
  // carate.carecommendation, so gating on rates alone flashes the rate then hides
  // it. See INDU-1480.
  get isCalendarPricingManagerDataLoading(): boolean {
    return (
      this.isPeriodsRequestLoading ||
      this.model.pricingAssistantRecommendationsRequestTaskResult.isRunning
    );
  }

  get isCalendarView(): boolean {
    return this.view === PRIMA_VIEWS.CALENDAR;
  }

  get isCompetitorsRatesRequestLoading(): boolean {
    return Boolean(
      this.pricingAssistantDataRequestService
        .pricingAssistantCompetitorRatesRequestTask?.isRunning ||
      this.model.pricingAssistantCompetitorsRatesRequestTaskResult?.isRunning,
    );
  }

  get isDayBarDataLoading(): boolean {
    return (
      this.model.pricingAssistantRatesRequestTaskResult.isRunning ||
      this.model.pricingAssistantGeneralDataRequestsTaskResult.isRunning
    );
  }

  get isPast(): boolean {
    return this.hotelAndMarketService
      .toLocalTime(this.month)
      .isBefore(
        this.hotelAndMarketService.getLocalTime().startOf('month'),
        'month',
      );
  }

  get isPeriodsRequestLoading(): boolean {
    return this.model.pricingAssistantRatesRequestTaskResult.isRunning;
  }

  get isRoomOffsetHierarchyStrategyLoading(): boolean {
    return Boolean(
      this.model.pricingAssistantroomOffsetHierarchyTaskResult?.isRunning,
    );
  }

  get isTablePricingManagerDataLoading(): boolean {
    return (
      this.isPeriodsRequestLoading ||
      this.isCompetitorsRatesRequestLoading ||
      this.isRoomOffsetHierarchyStrategyLoading ||
      this.model.pricingAssistantRecommendationsRequestTaskResult.isRunning
    );
  }

  get isTableView(): boolean {
    return this.view === PRIMA_VIEWS.TABLE;
  }

  get liveupdateEnabled(): boolean {
    return this.access.hasRatesCustomLiveupdate;
  }

  get maxDateMoment(): Dayjs {
    return this.hotelAndMarketService
      .getLocalTime()
      .add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month')
      .endOf('month');
  }

  // Pricing mode datepicker shouldn't allow months in the past
  get minDateMoment(): Dayjs {
    const minDate = this.hotelAndMarketService.getLocalTime().startOf('month');

    // For the table view it should show the the last week of the previous month if the week overlaps with the current month
    if (this.isTableView) {
      return minDate.startOf('week');
    }

    return minDate;
  }

  get monthMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(`${this.month}-15`);
  }

  get monthOnlyModel(): Period[] {
    if (this.isPeriodsRequestLoading) {
      return [];
    }

    return this.getPeriodsForDateRange(
      this.periods,
      this.monthMoment.startOf('month'),
      this.monthMoment.endOf('month').startOf('day'),
    );
  }

  get periods(): Period[] {
    if (this.isPeriodsRequestLoading) {
      const dateRangeStart = this.monthMoment.startOf('month').startOf('week');
      const dateRangeEnd = this.monthMoment
        .endOf('month')
        .endOf('week')
        .startOf('day');

      // Push a dummy period object for every day in range to have a clean loading state while periods request is loading #DUMMYPERIOD
      return getPeriodsInRange(dateRangeStart, dateRangeEnd, 'day').map(
        () => ({}),
      ) as Period[];
    }

    return this.model.pricingAssistantRatesRequestTaskResult.value ?? [];
  }

  get pmsSsEntryPointBannerMessage(): string {
    if (
      this.hotelAndMarketService.hotel.pmsExpectedOnboardingTime === undefined
    ) {
      return this.intl
        .t('pricing_assistant.self_service.calendar.message_no_days')
        .toString();
    }
    if (this.hotelAndMarketService.hotel.pmsExpectedOnboardingTime.days === 0) {
      return this.intl
        .t('pricing_assistant.self_service.calendar.message_less_than_day')
        .toString();
    }
    return this.intl
      .t('pricing_assistant.self_service.calendar.message_specified_days', {
        count: this.hotelAndMarketService.hotel.pmsExpectedOnboardingTime.days,
      })
      .toString();
  }

  get selectedOTA(): Ota | null {
    if (!this.otaId) {
      return null;
    }
    return this.store.peekRecord<Ota>('ota', this.otaId);
  }

  get selectedRoomCategory(): OpenRoomPricingFixedCategory {
    return selectedRoomCategory(
      this.roomType as OpenPricingRoomCategoryToRIRoomTypes,
    );
  }

  /* Show enable autopilot banner in case:
  - Autopilot banner Feature Flag is available (BE checks for admin user, last_integration_complete is older than a week ago and autopilot is enabled in settings)
  - Banner is not dismissed by the user
  */
  get showAutopilotBanner(): boolean {
    return (
      this.access.hasFeature('pricing_assistant.autopilot.banner') &&
      this.isAutopilotBannerNotDismissed
    );
  }

  get weekMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(`${this.week}`);
  }

  get weekOnlyModel(): { competitorsPeriods: Period[]; periods: Period[] } {
    if (
      this.isPeriodsRequestLoading ||
      this.isCompetitorsRatesRequestLoading ||
      !this.week
    ) {
      return { periods: [], competitorsPeriods: [] };
    }

    const startOfWeek = this.weekMoment.startOf('week');
    const endOfWeek = this.weekMoment.endOf('week').startOf('day');

    return {
      periods: this.getPeriodsForDateRange(
        this.periods,
        startOfWeek,
        endOfWeek,
      ),
      competitorsPeriods: this.getPeriodsForDateRange(
        this.competitorsPeriods,
        startOfWeek,
        endOfWeek,
      ),
    };
  }

  /**
   * Determines which week to display when switching to table view
   */
  determineWeekForTableView(): string {
    const currentDate = this.hotelAndMarketService.getLocalTime();

    // If user has selected a week, use that week
    if (this.isLastWeekSelectedValid()) {
      return this.lastWeekSelected!;
    }

    // If user is in current month, use current week
    if (this.month === currentDate.format('YYYY-MM')) {
      return currentDate.startOf('week').format('YYYY-MM-DD');
    }

    // If user is in another month, use first day (Sunday/Monday) of the month
    return this.hotelAndMarketService
      .toLocalTime(this.month)
      .startOf('month')
      .startOf('week')
      .format('YYYY-MM-DD');
  }

  getPeriodsForDateRange(
    periods: Period[],
    startDate: Dayjs,
    endDate: Dayjs,
  ): Period[] {
    /* Calculate the starting and end integer date as Unix time in "days".
    We take a clone of the date, set it to the start of the range,
    then get the Unix timestamp (in seconds) and adjust it by the UTC offset
    (converted to seconds by multiplying by 60). Finally, divide by 86400 (seconds in a day)
    and round down to get an integer value representing the day.*/
    const startUnix = Math.floor(
      (startDate.unix() + startDate.utcOffset() * 60) / 86400,
    );

    const endUnix = Math.floor(
      (endDate.unix() + endDate.utcOffset() * 60) / 86400,
    );

    return periods.filter(
      (period) =>
        period.day?.integerDate >= startUnix &&
        period.day?.integerDate <= endUnix,
    );
  }

  async handleSwitchToCalendarView(): Promise<void> {
    const currentDate = this.hotelAndMarketService.getLocalTime();

    // Use the month of the first day (Sunday/Monday) of the selected week
    const firstDayOfMonth = this.hotelAndMarketService
      .toLocalTime(this.week)
      .startOf('week')
      .startOf('month');

    // If first Sunday/Monday is in the past, return month of current week
    this.month = (
      firstDayOfMonth.isBefore(currentDate)
        ? currentDate.startOf('month')
        : firstDayOfMonth
    ).format('YYYY-MM');

    this.lastWeekSelected = this.week;
    this.week = null;

    await this.updateCalendarViewParameters();
  }

  handleSwitchToTableView(): void {
    this.week = this.determineWeekForTableView();

    // If first Sunday/Monday is in the previous month, update month to be in line with week
    if (this.week) {
      const firstDayOfWeek = this.hotelAndMarketService.toLocalTime(this.week);
      this.month = firstDayOfWeek.format('YYYY-MM');
    }

    this.updateTableViewParameters();
  }

  isLastWeekSelectedValid(): boolean {
    if (!this.lastWeekSelected) {
      return false;
    }

    const lastWeekSelected = this.hotelAndMarketService.toLocalTime(
      this.lastWeekSelected,
    );

    return lastWeekSelected
      ? lastWeekSelected.isSame(this.month, 'month')
      : false;
  }

  onAutopilotBannerClose(): void {
    this.localStorage.setItem(
      this.autopilotBannerlocalStorageKey,
      JSON.stringify(false),
    );

    // Update banner visibility state to trigger reactivity i.e. UI update
    this.bannerVisibilityStates[this.autopilotBannerlocalStorageKey] = false;
  }

  async updateCalendarViewParameters(): Promise<void> {
    this.updateGuests(
      this.hotelAndMarketService.hotel.paRatePreference.persons.toString(),
    );
    this.updateRoomType(
      this.hotelAndMarketService.hotel.paRatePreference.room_type.id,
    );
    this.updateRateCode(null);

    await this.updateRateParam(
      this.hotelAndMarketService.hotel.paRatePreference.rate_type as RateParam,
    );
    await this.updateMealPlan(
      this.hotelAndMarketService.hotel.paRatePreference
        .mealtype as MealTypeValues,
    );
    this.updatePlatform(DEFAULT_QP_VALUES.hotel['pricing-manager'].platform);

    validateOpenPricingCalendarParams(
      this.router.currentRoute?.queryParams,
      this.store,
      this.hotelAndMarketService,
      this.router,
    );
  }

  updateTableViewParameters(): void {
    this.updateGuests(
      this.hotelAndMarketService.hotel.paRatePreference.persons.toString(),
    );
    this.updateRoomType(null);

    if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
      this.updatePlatform(
        DEFAULT_QP_VALUES.hotel['pricing-manager'][
          'pricing-manager-dynamic-rate'
        ].platform,
      );
      this.updateRateCode(
        this.hotelAndMarketService.hotel.caxxxPmsSettings.pms_root_rate_id,
      );
    }
  }

  @action closeDayOverrideModal(): void {
    this.dayOverrideModalPeriod = null;
  }

  @action onPricingAssistantRequestsLoadingUpdate(): void {
    if (
      !this.access.hasPricingAssistantOpenPricingFeature &&
      this.arePricingPageModeRequestsLoading === false &&
      this.model.pricingAssistantRecommendationsRequestTaskResult.value
    ) {
      // Only perform the tracking task if there's actually a result/API request was successful
      void this.pricingAssistantDataRequestService.trackReturnedRecommendationsTask.perform(
        this.model.pricingAssistantRecommendationsRequestTaskResult.value,
      );
    }
  }

  @action openDayOverrideModal(period: Period): void {
    this.dayOverrideModalPeriod = period;
  }

  @action redirectToDayDetail(
    day: string,
    isEditActive: boolean = false,
  ): void {
    this.usageAnalytics.track('Day Detail Viewed', {
      eventTrigger: 'calendar-tile',
      product: 'pricing-manager',
      daysOffsetFromToday: differenceInDays(
        day,
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.pricing-manager.detail', {
      queryParams: { date: day, type: DETAIL_TABS.PRICING, edit: isEditActive },
    });
  }

  @action
  async refreshCompsetRates(): Promise<void> {
    const { dateRangeStart, dateRangeEnd, compareDate } =
      getCalendarMonthDateRanges(
        this.month as string,
        this.hotelAndMarketService.timezone,
      );

    const hotel = this.hotelAndMarketService.hotel;
    const leadingRoomId = hotel.caxxxPmsSettings.pms_leading_room_id;
    const cemaCategory = hotel.roomTypeMapping[
      leadingRoomId
    ] as OpenRoomPricingFixedCategory;

    const params = {
      compsetId: this.compsetId,
      los: this.los,
      maxPersons: this.maxPersons,
      mealPlan: String(this.mealPlan),
      membershipType: this.membershipType,
      month: this.month,
      otaId: this.otaId,
      platform: this.platform,
      rateCode: this.rateCode,
      rateParam: this.rateParam,
      roomType: this.roomType,
      refDaysAgo: this.refDaysAgo,
      view: this.view,
      week: this.week,
    };

    await this.pricingAssistantDataRequestService.pricingAssistantCompetitorRatesRequestTask.perform(
      hotel.id as string,
      dateRangeStart,
      dateRangeEnd,
      compareDate,
      cemaCategory,
      params,
    );
  }

  @action setBannerHeight(element: HTMLElement): void {
    this.bannerHeight = element.offsetHeight;
  }

  @action showBulkUpdateModal(): void {
    this.router.transitionTo('hotel.pricing-manager.bulk-update');
  }

  @action showCalendarSettingsModal(boolean: boolean): void {
    this.isShowingCalendarSettingsModal = boolean;
  }

  // Same logic as RI
  @action startUpdate(): void {
    if (this.liveupdateEnabled) {
      if (
        !this.liveupdate.isPolling &&
        !this.isPast &&
        this.selectedOTA?.hasLiveShop
      ) {
        if (!this.liveupdate.isMonthLiveupdateLimitReached) {
          // Note: in Rates we have a usage tracking event here. Removed this for the PA page for now (no v2 tracking yet)
          this.liveupdate.startLiveRateShop();
        }
      } else if (this.isPast) {
        void this.alerting.showErrorAlert({
          title: this.intl.t('error.liveupdate.historical_not_possible'),
          text: this.intl.t('error.liveupdate.to_date_from_date'),
        });
      }
    } else {
      void this.alerting.showErrorAlert({
        title: this.intl.t('error.liveupdate.disabled'),
        text: this.intl.t('error.liveupdate.not_allowed_for_hotel'),
      });
    }
  }

  @action
  async switchTab(viewSwitcherButtonId: string): Promise<void> {
    if (this.view === viewSwitcherButtonId) {
      return;
    }

    this.usageAnalytics.track('Data Visualisation Changed', {
      eventTrigger: 'filter-bar',
      from: this.isTableView ? 'Table' : 'Calendar',
      to: this.isTableView ? 'Calendar' : 'Table',
    });

    this.view = viewSwitcherButtonId;

    if (this.isTableView) {
      this.handleSwitchToTableView();
    } else if (this.isCalendarView && this.week) {
      await this.handleSwitchToCalendarView();
    }
  }

  @action updateGuests(maxPersons: string | null): void {
    this.maxPersons = maxPersons;
  }

  @action
  async updateMealPlan(mealPlan: MealTypeValues | null): Promise<void> {
    this.mealPlan = mealPlan;
    await this.refreshCompsetRates();
  }

  @action updatePageMode(targetPageMode: string): void {
    if (this.pageMode !== targetPageMode) {
      const subscriptionId = this.hotelAndMarketService.hotel.id;

      if (subscriptionId) {
        this.navigation.saveSubscription('hotel.rates.index', subscriptionId);
      }

      // Note: normally we did also pass the roomType value.
      // Removed it as a part of INDU-1354 to improve redirect behavior between PriMa -> Rates
      this.router.transitionTo('hotel.rates.index', {
        queryParams: {
          month: this.month,
          maxPersons: this.maxPersons,
        },
      });
    }
  }

  @action updatePlatform(platform: Platform): void {
    this.platform = platform;
  }

  @action updateRateCode(rateCode: string | null): void {
    this.rateCode = rateCode;
  }

  @action
  async updateRateParam(rateParam: RateParam | null): Promise<void> {
    this.rateParam = rateParam;
    await this.refreshCompsetRates();
  }

  @action updateRoomType(roomType: RoomType | null): void {
    this.roomType =
      OPEN_PRICING_ROOM_CATEGORY_TO_RI_ROOMTYPE_MAPPING[
        roomType as OpenPricingRoomCategoryToRIRoomTypes
      ] || roomType;
  }

  @action updateSelectedRange(selectedRange: DateRangeType): void {
    this.usageAnalytics.track('Date Range Changed', {
      eventTrigger: 'date-picker',
      product: 'pricing-manager',
    });

    const formattedMonth = selectedRange.start.format('YYYY-MM');
    const formattedWeek = selectedRange.start.format('YYYY-MM-DD');

    const hasMonthChanged = this.month !== formattedMonth;

    // Keep the week selected as long as the month doesn't change
    if (hasMonthChanged) {
      this.lastWeekSelected = null;
    }

    this.month = formattedMonth;

    if (this.isTableView) {
      this.week = formattedWeek;
    }
  }
}

export default HotelPricingManagerController;
